import React, { useState, useEffect } from "react";
import {
  Tabs,
  Row,
  Col,
  Spin,
  Card,
  Button,
  Form,
  Select,
  Input,
  Upload,
  message,
  Collapse,
  Tag,
  Alert,
} from "antd";
import dayjs from "dayjs";
import { CloudUploadOutlined, DownloadOutlined, ExportOutlined, TeamOutlined, UserOutlined, FireOutlined, FileAddOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchEnrollmentPolicyType, fetchEnrollmentDetails, fetchEnrollmenttype, createEnrollmentDataUpload, fetchEnrollmentUploadData, getSampleDocument, getEnrollmentSampleDocument, getEnrollmentDocumentData, getEnrollmentUploadExport } from "../../Action/Actions";
// UPDATED: Removed context import
// import { useSelectedYear } from "../../Context/SelectYearContext";
import { getUserData } from "../login/LoginSlice";
// UPDATED: Added imports for Redux financial year state
import { selectFinancialYears, selectSelectedYearId } from "../../Redux/financialYearSlice";
import { setSelectedPolicyData, selectPolicyTypeId, selectInsurerId } from "../../Redux/enrollmentPolicySlice";
import DataTable from "../DataTable/DataTable";
// import { saveAs } from "file-saver"; // Commented out - no longer needed with API-based export
// import * as XLSX from "xlsx"; // Commented out - no longer needed with API-based export

const RESTRICTED_POLICY_TYPE_IDS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];

const UploadEnrollment = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [enrollmentTypes, setEnrollmentTypes] = useState([]);
  const user = useSelector(getUserData);
  const AuthData = useSelector(getUserData);
  const [activeTab, setActiveTab] = useState("GMC");
  const [selectedPolicyNumber, setSelectedPolicyNumber] = useState(null);

  // UPDATED: Get financial year details from Redux instead of Context
  const selectedYearId = useSelector(selectSelectedYearId);
  const yearList = useSelector(selectFinancialYears);
  const selectedYear = yearList.find(year => year.id === selectedYearId)?.financial_year || "";

  // Get policy type ID and insurer ID from Redux
  const policyTypeIdFromRedux = useSelector(selectPolicyTypeId);
  const insurerIdFromRedux = useSelector(selectInsurerId);

  const [policyData, setPolicyData] = useState({});
  const [enrollmentDetailsData, setEnrollmentDetailsData] = useState({});
  const [selectedEnrollmentTypeId, setSelectedEnrollmentTypeId] = useState(null);
  const [uploadData, setUploadData] = useState([]);
  const [loading, setLoading] = useState(false);
  const policyTypes = Object.keys(policyData || {});
  const { Panel } = Collapse;
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();
  const { Dragger } = Upload;
  const [selectedPolicyid, setSelectedPolicyid] = useState(null);
  const [documentData, setDocumentData] = useState({});

  useEffect(() => {
    message.info("Please review the sample enrolment template document before uploading your file.", 5);
  }, []);

  // UPDATED: useEffect now depends on selectedYearId from Redux
  useEffect(() => {
    const fetchPolicyData = async () => {
      // Guard clause to prevent API calls without necessary IDs
      if (!AuthData?.corporate_id || !selectedYearId) {
        setLoading(false);
        setPolicyData({});
        setUploadData([]);
        setSelectedPolicyid(null);
        setSelectedPolicyNumber(null);
        return;
      }
      try {
        setLoading(true);
        // Fetch both policy type list and complete enrollment details
        const data = await dispatch(fetchEnrollmentPolicyType(AuthData.corporate_id));
        const enrollmentDetailsData = await dispatch(fetchEnrollmentDetails(AuthData.corporate_id));

        const { policy_type_list } = data;
        const { policy_enrollment_details } = enrollmentDetailsData;

        // NEW: Filter out restricted policy types from policy_type_list
        const filtered_policy_type_list = {};
        for (const type in policy_type_list) {
          const typeName = String(type).toLowerCase();
          if (typeName.includes('marine') || typeName.includes('fire')) {
            continue;
          }

          const policies = policy_type_list[type] || [];
          const validPolicies = policies.filter(p => {
            const details = Array.isArray(p.policy_details) ? p.policy_details[0] : p.policy_details;
            const typeId = details?.policy_type_id || p.policy_type_id || p.policy_details?.policy_type_id;
            const isRestrictedId = RESTRICTED_POLICY_TYPE_IDS.includes(Number(typeId));
            return !isRestrictedId;
          });
          if (validPolicies.length > 0) {
            filtered_policy_type_list[type] = validPolicies;
          }
        }

        // NEW: Filter out restricted policy types from policy_enrollment_details
        const filtered_policy_enrollment_details = {};
        for (const type in policy_enrollment_details) {
          const typeName = String(type).toLowerCase();
          if (typeName.includes('marine') || typeName.includes('fire')) {
            continue;
          }

          const policies = policy_enrollment_details[type] || [];
          const validPolicies = policies.filter(p => {
            const details = Array.isArray(p.policy_details) ? p.policy_details[0] : p.policy_details;
            const typeId = details?.policy_type_id || p.policy_type_id || p.policy_details?.policy_type_id;
            const isRestrictedId = RESTRICTED_POLICY_TYPE_IDS.includes(Number(typeId));
            return !isRestrictedId;
          });
          if (validPolicies.length > 0) {
            filtered_policy_enrollment_details[type] = validPolicies;
          }
        }

        setPolicyData(filtered_policy_type_list || {});
        setEnrollmentDetailsData(filtered_policy_enrollment_details || {});

        // Get policy types that actually have data (not empty arrays)
        const availableTabs = Object.keys(filtered_policy_type_list || {}).filter(
          (tab) => filtered_policy_type_list[tab] && filtered_policy_type_list[tab].length > 0
        );

        // Use the first available type that has data
        const tabToUse = availableTabs.length > 0 ? availableTabs[0] : "";

        // Update activeTab to the first available type with data
        if (tabToUse && tabToUse !== activeTab) {
          setActiveTab(tabToUse);
        }

        const firstPolicy = policy_type_list[tabToUse]?.[0];
        if (firstPolicy) {
          const policyId = firstPolicy.policy_id;
          // Use the same logic as filter: policy_number if available, otherwise policy_id
          const policyNumber = firstPolicy.policy_number?.trim() || firstPolicy.policy_id;
          setSelectedPolicyNumber(policyNumber);
          setSelectedPolicyid(policyId);

          // Get complete policy details from enrollment details and save to Redux
          // Note: Using == for comparison to handle string/number type differences
          const completePolicy = policy_enrollment_details?.[tabToUse]?.find(
            p => p.policy_details?.policy_id == policyId
          );
          if (completePolicy?.policy_details) {
            console.log('✅ Initial load - Updating Redux with policy details:', completePolicy.policy_details);
            dispatch(setSelectedPolicyData(completePolicy.policy_details));
          } else {
            console.warn('⚠️ Initial load - Could not find complete policy details for policy_id:', policyId);
          }

          if (policyId) {
            const uploadDataResponse = await dispatch(fetchEnrollmentUploadData(policyId, pagination.current));
            // Use Enrollment_upload_data (capital E) - the actual API response key
            setUploadData(uploadDataResponse?.Enrollment_upload_data?.data || []);
            setPagination(prev => ({
              ...prev,
              current: uploadDataResponse?.Enrollment_upload_data?.current_page || 1,
              total: uploadDataResponse?.Enrollment_upload_data?.total || 0,
            }));
          }
        } else {
          setSelectedPolicyid(null);
          setSelectedPolicyNumber(null);
          setUploadData([]);
        }

        const enrollmentData = await dispatch(fetchEnrollmenttype(AuthData.corporate_id));
        setEnrollmentTypes(enrollmentData?.Policy_Details || []);
      } catch (err) {
        console.error("Error fetching policy data:", err);
        setPolicyData({}); // Clear data on error
      } finally {
        setLoading(false);
      }
    };

    fetchPolicyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, AuthData?.corporate_id, selectedYearId]);

  useEffect(() => {
    const fetchDocumentData = async () => {
      if (!uploadData || uploadData.length === 0) return;
      try {
        setLoading(true);
        const documentPromises = uploadData.map((record) =>
          dispatch(getEnrollmentDocumentData(record.id))
            .then((data) => ({ id: record.id, url: data?.Enrollment_upload_data }))
            .catch(() => ({ id: record.id, url: null }))
        );

        const documentResults = await Promise.all(documentPromises);
        const documentMap = documentResults.reduce((acc, { id, url }) => {
          acc[id] = url;
          return acc;
        }, {});

        setDocumentData(documentMap);
      } catch (error) {
        console.error("Error fetching document data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocumentData();
  }, [dispatch, uploadData]);

  const handleTabChange = async (key) => {
    setActiveTab(key);

    // Reset enrollment type selection when tab changes
    setSelectedEnrollmentTypeId(null);
    form.resetFields(['enrollmentType']);

    // Automatically select and fetch data for the first policy of the new tab
    const firstPolicy = policyData[key]?.[0];
    if (firstPolicy) {
      const policyNumber = firstPolicy.policy_number?.trim() || firstPolicy.policy_id;
      const policyId = firstPolicy.policy_id;
      setSelectedPolicyNumber(policyNumber);
      setSelectedPolicyid(policyId);

      // Get complete policy details from enrollment details and save to Redux
      // Note: Using == for comparison to handle string/number type differences
      const completePolicy = enrollmentDetailsData?.[key]?.find(
        p => p.policy_details?.policy_id == policyId
      );
      if (completePolicy?.policy_details) {
        console.log('✅ Tab changed - Updating Redux with policy details:', completePolicy.policy_details);
        dispatch(setSelectedPolicyData(completePolicy.policy_details));
      } else {
        console.warn('⚠️ Tab changed - Could not find complete policy details for policy_id:', policyId);
      }

      // Fetch upload data for the first policy
      if (policyId) {
        try {
          setLoading(true);
          const uploadDataResponse = await dispatch(fetchEnrollmentUploadData(policyId, 1));
          setUploadData(uploadDataResponse?.Enrollment_upload_data?.data || []);
          setPagination(prev => ({
            ...prev,
            current: uploadDataResponse?.Enrollment_upload_data?.current_page || 1,
            total: uploadDataResponse?.Enrollment_upload_data?.total || 0,
          }));
        } catch (error) {
          console.error("Error fetching enrollment upload data:", error);
          setUploadData([]);
        } finally {
          setLoading(false);
        }
      }
    } else {
      // No policies in this tab, clear data
      setSelectedPolicyNumber(null);
      setSelectedPolicyid(null);
      setUploadData([]);
      setPagination(prev => ({ ...prev, current: 1, total: 0 }));
    }
  };

  const handlePolicyNumberClick = async (key) => {
    const selectedPolicy = policyData[activeTab]?.find(p => {
      const policyKey = p.policy_number?.trim() || p.policy_id;
      return policyKey === key;
    });
    if (selectedPolicy) {
      const policyNumber = selectedPolicy.policy_number?.trim() || selectedPolicy.policy_id;
      const policyId = selectedPolicy.policy_id;
      setSelectedPolicyNumber(policyNumber);
      setSelectedPolicyid(policyId);

      // Reset enrollment type selection when policy changes
      setSelectedEnrollmentTypeId(null);
      form.resetFields(['enrollmentType']);

      // Find and save complete policy details to Redux
      // Note: Using == instead of === because policy_id might be string in one API and number in another
      const completePolicy = enrollmentDetailsData?.[activeTab]?.find(
        p => p.policy_details?.policy_id == policyId
      );
      if (completePolicy?.policy_details) {
        console.log('✅ Updating Redux with policy details for policy:', policyId, completePolicy.policy_details);
        dispatch(setSelectedPolicyData(completePolicy.policy_details));
      } else {
        console.warn('⚠️ Could not find complete policy details for policy_id:', policyId);
      }

      // Fetch upload data for the selected policy
      if (policyId) {
        try {
          setLoading(true);
          const uploadDataResponse = await dispatch(fetchEnrollmentUploadData(policyId, 1));
          setUploadData(uploadDataResponse?.Enrollment_upload_data?.data || []);
          setPagination(prev => ({
            ...prev,
            current: uploadDataResponse?.Enrollment_upload_data?.current_page || 1,
            total: uploadDataResponse?.Enrollment_upload_data?.total || 0,
          }));
        } catch (error) {
          console.error("Error fetching enrollment upload data:", error);
          setUploadData([]);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleTableChange = async (pagination) => {
    if (selectedPolicyid) {
      try {
        setLoading(true);
        const uploadDataResponse = await dispatch(fetchEnrollmentUploadData(selectedPolicyid, pagination.current));
        setUploadData(uploadDataResponse?.Enrollment_upload_data?.data || []);
        setPagination(prev => ({
          ...prev,
          current: uploadDataResponse?.Enrollment_upload_data?.current_page || pagination.current,
          total: uploadDataResponse?.Enrollment_upload_data?.total || 0,
        }));
      } catch (error) {
        console.error("Error fetching enrollment upload data:", error);
        setUploadData([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDownloadSample = async () => {
    setLoading(true);
    try {
      console.log('🔍 Download Sample clicked - Current Redux state:', {
        insurerId: insurerIdFromRedux,
        policyTypeId: policyTypeIdFromRedux,
        selectedPolicyId: selectedPolicyid
      });

      // Validation: Check if enrollment type is selected
      if (!selectedEnrollmentTypeId) {
        message.error("Please select an enrollment type first");
        setLoading(false);
        return;
      }

      // Get insurer_id and policy_type_id from Redux
      const insurerId = insurerIdFromRedux;
      const policyTypeId = policyTypeIdFromRedux;

      // Validation: Check if all required parameters are available
      if (!insurerId || !policyTypeId) {
        console.error('❌ Missing required parameters:', { insurerId, policyTypeId });
        message.error("Policy details not available. Please select a policy first.");
        setLoading(false);
        return;
      }

      console.log('📤 Calling getEnrollmentSampleDocument with:', {
        insurerId,
        policyTypeId,
        enrollmentTypeId: selectedEnrollmentTypeId
      });

      // Try the new API with all three parameters
      try {
        const result = await dispatch(
          getEnrollmentSampleDocument(insurerId, policyTypeId, selectedEnrollmentTypeId)
        );

        if (result?.document) {
          // Open the document in a new tab
          window.open(result.document, "_blank");
          message.success("Sample document downloaded successfully based on Insurer");
        } else {
          message.error("No document URL found in response");
        }
      } catch (apiError) {
        // Log the full error for debugging
        console.log('🔴 New API Error Details:', {
          message: apiError?.message,
          status: apiError?.status,
          responseStatus: apiError?.response?.status,
          fullError: apiError
        });

        // Check if error is 400 Bad Request - fallback to old implementation
        const is400Error = apiError?.message?.includes('400') ||
          apiError?.response?.status === 400 ||
          apiError?.status === 400;

        console.log('🔍 Is 400 Error?', is400Error);

        if (is400Error) {
          console.warn('⚠️ New API returned 400 - Falling back to old implementation');

          // Fallback to old hardcoded implementation
          try {
            console.log('📞 Calling fallback API: getSampleDocument(8)');
            const actionResult = await dispatch(getSampleDocument(8));
            const res = actionResult.payload || actionResult;
            let documentUrl = res?.Sample_document?.document ||
              (Array.isArray(res?.Sample_document) && res.Sample_document[0]?.document);

            console.log('📄 Fallback API response:', { documentUrl });

            if (documentUrl) {
              window.open(documentUrl, "_blank");
              message.success("Sample document downloaded successfully");
            } else {
              message.error("No valid document URL found");
            }
          } catch (fallbackError) {
            console.error("❌ Fallback Download Error:", fallbackError);
            message.error("Failed to download sample file");
          }
        } else {
          // Other errors - show error message
          console.error("❌ Download Sample Error (not 400):", apiError);
          const errorMessage = apiError?.message || "Failed to download sample file";
          message.error(errorMessage);
        }
      }
    } catch (err) {
      console.error("Unexpected Download Sample Error:", err);
      message.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!values.upload_document || values.upload_document.length === 0) {
      message.error("Please select a file to upload.");
      return;
    }
    const formData = new FormData();
    formData.append("ref_policy_type_id", 1); // This seems static, check if it should be dynamic
    formData.append("policy_type", activeTab);
    formData.append("ref_policy_id", selectedPolicyid);
    formData.append("policy_number", selectedPolicyNumber);
    formData.append("enrollment_type", values.enrollmentType);
    formData.append("ref_enrollment_type_id", 1); // This also seems static
    formData.append("description", values.description || "");
    formData.append("doc_upload", values.upload_document[0].originFileObj);

    try {
      setLoading(true);
      const response = await dispatch(createEnrollmentDataUpload(formData));

      // Handle successful response
      if (response?.status === true && response?.message) {
        message.success(response.message);
        form.resetFields();
        setFileList([]);

        // Re-fetch upload data for the current policy to show the new entry
        const updatedUploadData = await dispatch(fetchEnrollmentUploadData(selectedPolicyid, 1));
        setUploadData(updatedUploadData?.Enrollment_upload_data?.data || []);
        setPagination(prev => ({
          ...prev,
          current: 1,
          total: updatedUploadData?.Enrollment_upload_data?.total || 0,
        }));
      }
    } catch (error) {
      console.error("Error creating enrollment data:", error);

      // Handle different error scenarios
      if (error?.statusCode === 400 || error?.statusCode === 422) {
        message.error("Failed to upload enrollment document.");
      } else if (error?.message) {
        // Display the error message from the API
        message.error(error.message);

        // If there are validation errors, display them
        if (error?.error) {
          const validationErrors = error.error;

          // If it's an object of validation errors, display each one
          if (typeof validationErrors === "object" && !Array.isArray(validationErrors)) {
            Object.keys(validationErrors).forEach((field) => {
              const fieldErrors = validationErrors[field];
              if (Array.isArray(fieldErrors)) {
                fieldErrors.forEach((err) => message.error(`${field}: ${err}`));
              } else {
                message.error(`${field}: ${fieldErrors}`);
              }
            });
          } else if (Array.isArray(validationErrors)) {
            // If it's an array of errors
            validationErrors.forEach((err) => message.error(err));
          }
        }
      } else {
        // Fallback error message
        message.error("An error occurred while creating enrollment data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Old frontend export function - Commented out as per requirement
  // const handleExport = () => {
  //   const dataToExport = uploadData.map((data, index) => ({
  //     "SI NO": (pagination.current - 1) * pagination.pageSize + index + 1,
  //     "Enrollment Type": data.enrollment_type || "N/A",
  //     "Description": data.description || "N/A",
  //     "Document": data.enrollment_doc_upload || "N/A",
  //     "Uploaded At": data.created_at ? dayjs(data.created_at).format('D MMM, YYYY HH:mm A') : "N/A",
  //   }));

  //   const worksheet = XLSX.utils.json_to_sheet(dataToExport);
  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(workbook, worksheet, "Enrollment Data");

  //   const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  //   const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  //   saveAs(blob, `Enrollment_Data_${selectedYear}.xlsx`);
  // };

  // New API-based export function
  const handleExport = async () => {
    if (!selectedPolicyid) {
      message.error("Please select a policy to export data.");
      return;
    }

    try {
      setLoading(true);
      const downloadLink = await dispatch(getEnrollmentUploadExport(selectedPolicyid));

      if (downloadLink) {
        // Open the download link in a new tab to trigger download
        window.open(downloadLink, "_blank");
        message.success("Export file is being downloaded.");
      } else {
        message.error("Failed to get download link.");
      }
    } catch (error) {
      console.error("Export Error:", error);
      message.error(error || "Failed to export enrollment upload data.");
    } finally {
      setLoading(false);
    }
  };

  const getTabIcon = (type) => {
    switch (type) {
      case "GMC": return <TeamOutlined style={{ marginRight: 8 }} />;
      case "GTL": return <UserOutlined style={{ marginRight: 8 }} />;
      case "GPA": return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Parent policy": return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Topup": return <FileAddOutlined style={{ marginRight: 8 }} />;
      case "Fire": return <FireOutlined style={{ marginRight: 8 }} />;
      default: return null;
    }
  };

  const columns = [
    {
      title: "SI NO",
      key: "si_no",
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    { title: "ENROLLMENT TYPE", dataIndex: "enrollment_type", key: "enrollment_type" },
    { title: "DESCRIPTION", dataIndex: "description", key: "description" },
    {
      title: "VIEW DOCUMENT",
      key: "view_document",
      render: (_, record) => (
        <Button
          className="d-flex-table"
          onClick={() => {
            const documentUrl = documentData[record.id];
            if (documentUrl) {
              window.open(documentUrl, "_blank");
            }
          }}
          type="button"
          disabled={!documentData[record.id]}
          style={{ background: "#1890ff", color: "white" }}
        >
          View Document
        </Button>
      ),
    },
    {
      title: "UPLOADED AT",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => (text ? dayjs(text).format("D MMM, YYYY HH:mm A") : "N/A"),
    },
  ];

  const tabItems = policyTypes?.map((type) => ({
    key: type,
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        {type} {getTabIcon(type)}
      </span>
    ),
    children: (
      <>
        <Tabs
          activeKey={selectedPolicyNumber}
          onChange={handlePolicyNumberClick}
          // UPDATED: Removed fallback logic as API guarantees policy_number
          items={policyData[type]?.map((policy) => {
            const key = policy.policy_number?.trim() || policy.policy_id;
            return {
              key: key,
              label: policy.policy_number?.trim() ? (
                policy.policy_number.trim()
              ) : (
                <Tag
                  style={{
                    backgroundColor: "#FFFF00",
                    color: "#000000E0",
                    fontSize: "12px",
                    padding: "2px 8px",
                    margin: 0,
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    borderRadius: "4px",
                  }}
                >
                  Policy Number not issued
                </Tag>
              ),
            };
          }) || []}
        />
        {selectedPolicyNumber ? (
          <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
            <Col span={24}>
              <Card style={{ width: "100%" }}>
                <Alert
                  message="Please review the sample enrolment template document before uploading your file."
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  style={{
                    marginBottom: '20px',
                    borderRadius: '8px',
                    backgroundColor: '#e6f7ff',
                    border: '1px solid #91d5ff'
                  }}
                />
                <Form layout="vertical" className="mt-3" form={form} onFinish={handleSubmit}>
                  <Row gutter={16}>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item label="Enrollment Type" name="enrollmentType" rules={[{ required: true, message: "Please select the enrollment type!" }]}>
                        <Select
                          placeholder="Select Type"
                          onChange={(value) => {
                            // Find and save the enrollment type ID when user selects
                            const selected = enrollmentTypes.find(et => et.enrollment_type === value);
                            if (selected) {
                              setSelectedEnrollmentTypeId(selected.id);
                            }
                          }}
                        >
                          {enrollmentTypes.map((et) => (<Select.Option key={et.id} value={et.enrollment_type}>{et.enrollment_type}</Select.Option>))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item label="Description" name="description">
                        <Input.TextArea rows={1} placeholder="Enter Description" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Form.Item label="Upload Document" name="upload_document" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e && e.fileList} rules={[{ required: true, message: "Please Upload Document!" }]}>
                        <Dragger maxCount={1} listType="picture" beforeUpload={() => false} accept=".csv,.xlsx,.xls">
                          <p className="ant-upload-drag-icon"><CloudUploadOutlined /></p>
                          <p className="ant-upload-text">Drag and drop a file here or click</p>
                        </Dragger>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item>
                        <Row gutter={16}>
                          <Col xs={24} sm={12}>
                            <Button className="upload-data-btn" type="primary" htmlType="submit" style={{ width: '100%' }}>Upload Data</Button>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Button className="download-sample-btn" type="primary" onClick={handleDownloadSample} icon={<DownloadOutlined />} style={{ width: '100%' }}>Download Sample</Button>
                          </Col>
                        </Row>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
                <Row gutter={16} className="mb-3 justify-content-end align-items-center px-3">
                  <Col>
                    <Button type="primary" icon={<ExportOutlined />} className="d-flex" onClick={handleExport}>Export</Button>
                  </Col>
                </Row>
                <DataTable columns={columns} dataSource={uploadData} loading={loading} rowKey="id" pagination={pagination} onChange={handleTableChange} />
              </Card>
            </Col>
          </Row>
        ) : (
          !loading && <div className="escalation-message">No Upload Enrollment Data Found.</div>
        )}
      </>
    ),
  }));

  return (
    <Spin spinning={loading}>
      <div className="escalation-matrix-container">
        <div className="escalation-content">
          <Row className="escalation-row-header">
            <span style={{ fontWeight: "700", fontSize: "20px" }}>
              {user?.corporate_name || "Guest"}
            </span>{" "}
            {/* UPDATED: This now correctly displays the financial year from Redux */}
            <span style={{ marginLeft: "8px" }}>
              (Financial Year: {selectedYear})
            </span>
          </Row>
        </div>
        {policyTypes.length > 0 ? (
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            className="esc-tab"
            items={tabItems}
          />
        ) : (
          <Card style={{ marginTop: "20px" }}>
            <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
              <p style={{ fontSize: "16px", fontWeight: "500" }}>No enrollment data available for {selectedYear}</p>
            </div>
          </Card>
        )}
      </div>
    </Spin>
  );
};

export default UploadEnrollment;